/**
 * features/finance/hooks/useTransferVerifications.ts
 *
 * Puente de datos del módulo "Verificación de transferencias" (Bloque A).
 * Lee transfer_verifications + eventos (RLS: acceso por tienda o
 * payments.verify_transfer) y ejecuta las acciones vía RPC server-authoritative
 * (aprobar/rechazar/investigar/comentar/extender). El servidor es la autoridad;
 * este hook nunca calcula dinero ni cambia estados por su cuenta.
 */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@lib/supabase/client'
import { bumpSales } from '@shared/hooks/useDataRefresh'

export interface TransferVerification {
  id: string
  code: string
  store_id: string
  shift_id: string
  client_id: string | null
  created_by: string
  status:
    | 'pendiente_revision'
    | 'por_investigar'
    | 'aprobada'
    | 'rechazada'
    | 'confirmada_manualmente'
  reference: string
  evidence_url: string | null
  amount: number
  subtotal: number
  tax: number
  discount: number
  observations: string | null
  resolution_reason: string | null
  confirmed_by: string | null
  confirmation_method: string | null
  authorized_by: string | null
  authorization_reason: string | null
  resolved_by: string | null
  resolved_at: string | null
  sale_id: string | null
  review_deadline: string | null
  expires_at: string
  created_at: string
  updated_at: string
}

export interface TransferEvent {
  id: string
  verification_id: string
  event_type: string
  actor_name: string | null
  actor_role: string | null
  note: string | null
  created_at: string
}

export type AlertLevel = 'none' | 'attention' | 'escalated'

// Alerta por antigüedad SOLO para casos abiertos (por investigar / pendiente).
// 24h → atención; 72h → escalada. La reserva NUNCA se libera automáticamente.
export function alertLevel(v: Pick<TransferVerification, 'status' | 'created_at'>): AlertLevel {
  if (v.status !== 'por_investigar' && v.status !== 'pendiente_revision') return 'none'
  const ageH = (Date.now() - new Date(v.created_at).getTime()) / 3_600_000
  if (ageH >= 72) return 'escalated'
  if (ageH >= 24) return 'attention'
  return 'none'
}

export function ageHours(createdAt: string): number {
  return (Date.now() - new Date(createdAt).getTime()) / 3_600_000
}

export type TabKey =
  | 'pendientes'
  | 'por_investigar'
  | 'atencion'
  | 'escaladas'
  | 'aprobadas'
  | 'rechazadas'
  | 'todas'

export interface TransferFilters {
  storeId?: string
  reference?: string
  seller?: string
  confirmedBy?: string
  minAmount?: number
  maxAmount?: number
  from?: string
  to?: string
}

// ── Acceso: ¿el usuario puede resolver transferencias? (RBAC autoritativo) ──
export function useTransferVerifyAccess() {
  const [allowed, setAllowed] = useState(false)
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const { data } = await supabase.rpc('my_permissions')
        if (cancelled) return
        const perms = (data ?? []) as Array<{ permission_key: string; allowed: boolean }>
        setAllowed(perms.some(r => r.permission_key === 'payments.verify_transfer' && r.allowed))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])
  return { allowed, loading }
}

// ── Autorizadores excepcionales elegibles (servidor: activos, en alcance, rango) ──
export interface TransferAuthorizer {
  id: string
  name: string
  role: string
}
export function useTransferAuthorizers(storeId: string | null, enabled: boolean) {
  const [authorizers, setAuthorizers] = useState<TransferAuthorizer[]>([])
  const [loading, setLoading] = useState(false)
  useEffect(() => {
    if (!enabled || !storeId) {
      setAuthorizers([])
      return
    }
    let cancelled = false
    setLoading(true)
    void (async () => {
      try {
        const { data } = await supabase.rpc('list_transfer_authorizers', { p_store_id: storeId })
        if (!cancelled) setAuthorizers((data ?? []) as TransferAuthorizer[])
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [storeId, enabled])
  return { authorizers, loading }
}

// ── Lista de verificaciones ─────────────────────────────────────────────────
export function useTransferVerifications(filters: TransferFilters) {
  const [rows, setRows] = useState<TransferVerification[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    let q = supabase
      .from('transfer_verifications')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(500)
    if (filters.storeId) q = q.eq('store_id', filters.storeId)
    if (filters.reference) q = q.ilike('reference', `%${filters.reference}%`)
    if (filters.confirmedBy) q = q.ilike('confirmed_by', `%${filters.confirmedBy}%`)
    if (filters.minAmount != null) q = q.gte('amount', filters.minAmount)
    if (filters.maxAmount != null) q = q.lte('amount', filters.maxAmount)
    if (filters.from) q = q.gte('created_at', filters.from)
    if (filters.to) q = q.lte('created_at', filters.to)
    const { data, error: qErr } = await q
    if (qErr) {
      setError(qErr.message)
      setRows([])
    } else {
      setRows((data ?? []) as TransferVerification[])
    }
    setLoading(false)
  }, [
    filters.storeId,
    filters.reference,
    filters.confirmedBy,
    filters.minAmount,
    filters.maxAmount,
    filters.from,
    filters.to,
  ])

  useEffect(() => {
    void load()
  }, [load])

  return { rows, loading, error, reload: load }
}

export function filterByTab(rows: TransferVerification[], tab: TabKey): TransferVerification[] {
  switch (tab) {
    case 'pendientes':
      return rows.filter(r => r.status === 'pendiente_revision' || r.status === 'por_investigar')
    case 'por_investigar':
      return rows.filter(r => r.status === 'por_investigar')
    case 'atencion':
      return rows.filter(r => alertLevel(r) === 'attention')
    case 'escaladas':
      return rows.filter(r => alertLevel(r) === 'escalated')
    case 'aprobadas':
      return rows.filter(r => r.status === 'aprobada')
    case 'rechazadas':
      return rows.filter(r => r.status === 'rechazada')
    case 'todas':
    default:
      return rows
  }
}

// ── Eventos (historial / timeline) ──────────────────────────────────────────
export async function fetchTransferEvents(verificationId: string): Promise<TransferEvent[]> {
  const { data } = await supabase
    .from('transfer_verification_events')
    .select('*')
    .eq('verification_id', verificationId)
    .order('created_at', { ascending: true })
  return (data ?? []) as TransferEvent[]
}

// ── Acciones (todas server-authoritative vía RPC) ───────────────────────────
type ActionResult = { ok: true; data: unknown } | { ok: false; code: string; message: string }

function mapRpcErr(message: string): { code: string; message: string } {
  const code = message.split(':')[0]?.trim() ?? 'ERROR'
  return { code, message }
}

export function useTransferActions() {
  const [submitting, setSubmitting] = useState(false)

  const run = useCallback(
    async (fn: string, args: Record<string, unknown>): Promise<ActionResult> => {
      setSubmitting(true)
      const { data, error } = await supabase.rpc(fn, args)
      setSubmitting(false)
      if (error) return { ok: false, ...mapRpcErr(error.message ?? 'Error') }
      return { ok: true, data }
    },
    []
  )

  return {
    submitting,
    approve: async (id: string, observations?: string) => {
      const res = await run('pos_approve_transfer_verification', {
        p_verification_id: id,
        p_observations: observations ?? null,
      })
      // Aprobar convierte la verificación en VENTA ⇒ recargar vistas de ventas.
      // Rechazar/investigar/comentar/extender NO crean venta ⇒ sin bump.
      if (res.ok) bumpSales()
      return res
    },
    reject: (id: string, reason: string) =>
      run('pos_reject_transfer_verification', { p_verification_id: id, p_reason: reason }),
    investigate: (id: string, observations?: string) =>
      run('pos_mark_transfer_investigating', {
        p_verification_id: id,
        p_observations: observations ?? null,
      }),
    comment: (id: string, note: string) =>
      run('pos_comment_transfer_verification', { p_verification_id: id, p_note: note }),
    extend: (id: string, newDeadline: string, reason: string) =>
      run('pos_extend_transfer_verification', {
        p_verification_id: id,
        p_new_deadline: newDeadline,
        p_reason: reason,
      }),
  }
}
